import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  IMeasurement,
  IMeasurementsFilters,
  IMeasurementTypeValidation,
} from "../../../@types/admin/AdminMeasurement";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { measurementsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Query key the mutations invalidate — every page and filter combination. */
export const MEASUREMENTS_QUERY_KEY = "admin-measurements";

/**
 * A page of measurements. Server-paginated: this list spans every patient, so
 * unlike the catalogue-sized admin pages it can't be filtered client-side.
 *
 * `enabled` keeps it idle until the operator asks. Retool's table said "Press
 * find to start ( under maintenance )" and only ran the query on the Find
 * button, because an unfiltered first page across all patients is a slow read
 * (its own query timeout was 60s).
 */
export function useMeasurements(
  filters: IMeasurementsFilters,
  enabled: boolean
) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IPaginatedResponse<IMeasurement[]>
  >({
    queryKey: [MEASUREMENTS_QUERY_KEY, filters],
    queryFn: async () =>
      ensureOk(await measurementsService.getMeasurements(filters)),
    enabled,
    // Keeps the current page on screen while the next one loads, instead of
    // blanking the table on every page step.
    placeholderData: keepPreviousData,
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return {
    measurements: data?.result ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
  };
}

/**
 * Measurement types with the min/max the edit form validates against. Loaded
 * with the page rather than with the modal — the archive confirmation labels
 * its values from the same catalogue.
 */
export function useMeasurementTypeValidations() {
  const { data, isLoading, isError } = useQuery<IMeasurementTypeValidation[]>({
    queryKey: ["admin-measurement-type-validations"],
    queryFn: async () =>
      ensureOk(await measurementsService.getMeasurementTypes()),
  });

  return { measurementTypes: data ?? [], isLoading, isError };
}
